import { DateRange, dateRangesOverlap } from 'owa-datetime-utils';
import { EventEntity } from '../schema/EventEntity';
import { EventsRetentionInfo } from '../types/EventsRetentionInfo';
import { getIndexRangeForEventsInDateRange } from '../utils/getIndexRangeForEventsInDateRange';
import { isInAnyOfDateRanges } from '../utils/isInAnyOfDataRanges';

export function evictEventsInDateRange<T extends EventEntity>(
    dateRangeToEvict: DateRange,
    events: T[],
    eventsRetentionInfo: EventsRetentionInfo
): T[] {
    const { dateRangesToRetain, eventsToRetain } = eventsRetentionInfo;

    // We are only concerned with overlapping ranges and not disjointed ranges that are
    // out of the scope of the dateRangeToEvict
    const overlappingRanges = dateRangesToRetain.filter(
        range => dateRangesOverlap(range, dateRangeToEvict, true /* inclusive */) === 0
    );

    // As we are evicting a certain date range hence we only care of events between
    // that date range
    const indexRange = getIndexRangeForEventsInDateRange(events, dateRangeToEvict);

    return evictEventsThatDontNeedToBeRetained(
        events,
        { dateRangesToRetain: overlappingRanges, eventsToRetain },
        indexRange
    );
}

interface EventsToRemoveBatch {
    startIndex: number;
    removeCount: number;
    finished: boolean;
}

function evictEventsThatDontNeedToBeRetained<T extends EventEntity>(
    events: T[],
    eventsRetentionInfo: EventsRetentionInfo,
    indexes: { startIndex: number; endIndex: number }
) {
    const { dateRangesToRetain, eventsToRetain } = eventsRetentionInfo;
    const { startIndex, endIndex } = indexes;
    const eventsToRemoveBatches: EventsToRemoveBatch[] = [
        { startIndex: startIndex, removeCount: 0, finished: false },
    ];
    let currentBatch = 0;

    // While iterating we are assuming that the events are stored in
    // order of the start date
    for (let i = startIndex; i >= 0 && i <= endIndex && i <= events.length; i++) {
        const currentEvent = events[i];
        if (
            eventsToRetain[currentEvent.Key] ||
            isInAnyOfDateRanges(currentEvent, dateRangesToRetain)
        ) {
            // If the current event date range overlaps with date ranges to retain
            // or the event is specifically one of the events to retain then consider
            // the current range to be finised so that a new range be created next time
            eventsToRemoveBatches[currentBatch].finished = true;
        } else if (!eventsToRemoveBatches[currentBatch].finished) {
            eventsToRemoveBatches[currentBatch].removeCount += 1;
        } else {
            eventsToRemoveBatches.push({
                startIndex: i,
                removeCount: 1,
                finished: false,
            });
            currentBatch += 1;
        }
    }

    // As we are removing the items so we have to start from the end indexes first
    // so that earlier indexes in eventsToRemove don't get invalidated
    const evictedEvents: T[] = [];

    eventsToRemoveBatches
        .filter(e => e.removeCount !== 0)
        .reverse() // We reverse it so that we remove the list of events from the end so that the startIndex computed before stay correct
        .forEach(e => {
            const removedEvents = events.splice(e.startIndex, e.removeCount);
            evictedEvents.push(...removedEvents.reverse());
        });

    // As the events were deleted from end hence we return
    // the evicted events in the sorted order.
    return evictedEvents.reverse();
}
